import { DataFrame } from '../data/DataFrame.js';
import { UiElement } from '../ui/UiElement.js';

export { Plot }

/**
 * An abstraction for Plotly plots with default styling and dataframe handling.
 * 
 * Events:
 * * ready
 * * legend fired for selection / deselection of columns
 * * scaled fired for x- and y-scale changes
 * 
 * Check out supported plot types.
 * 
 * | name         | type       | mode       | comment                                                             |
 * | :---------   | :--------- | :--------- | :------------------------------------------------------------------ |
 * | line chart   | scatter    | lines      | you can use lines+markers as mode                                   |
 * | scatter plot | scatter    | markers    | you can use lines+markers as mode                                   |
 * | area chart   | scatter    | lines      | you have to set column attribute "fill" to "tozeroy" or "tonexty"   |
 * | bar chart    | bar        | -          |                                                                     |
 * | pie chart    | pie        | -          | numeric series values are summed up and mapped against column names |
 * | box plot     | box        | -          | TODO: use cached dataframe statistics                               |
 * | histogram    |            | -          | TODO: not yet implemented                                           |
 * | heat map     |            | -          | TODO: not yet implemented                                           |
 * | contour plot |            | -          | TODO: not yet implemented                                           |
 * 
 * @author rkoppe <roland.koppe@awi.de>
 * @author rhess <robin.hess@awi.de>
 * 
 * @memberof vef.plot
 */
class Plot extends UiElement {

    options = {
        trace: {
            id: '',
            name: '',
            mode: 'markers',
            yaxis: 'y'
        },
        plot: {
            type: 'scatter'
        },
        layout: {
            xaxis: {
                domain: [0, 1]
            },
            yaxis: {
                title: 'yaxis'
            },
            yaxis2: {
                title: '',
                side: 'right',
                overlaying: 'y',
                position: .9
            },
            yaxis3: {
                title: '',
                side: 'right',
                overlaying: 'y',
                position: .8
            }
        },
        config: {
            responsive: true
        }
    };

    initialized = false;
    data = [];

    selected = {};

    /**
     * Initializes the plot for the element defined by id and given options.
     * 
     * @param {HTMLElement | string} target 
     * @param {object} options 
     */
    constructor(target, options) {
        super(target, {
            'ready': [],
            'legend': [],
            'scaled': []
        });

        this.options = Object.assign(this.options, options);
        this.options.trace.type = this.options.plot.type;
    }

    /**
     * Plots the dataframe columns defined by y with optional
     * x-axis and z-axis. Columns for y-axes can be defined as strings or
     * objects. Objects can be used to style the y-axes. There<fore 
     * 
     * ```
     * y: {
     *   id: '',    -- optional
     *   name: '',  -- name of column in dataframe
     *   title: '', -- optional
     *   mode: '',  -- select mode for line chart, scatter plot, area chart
     *   yaxis: ''  -- optional, define name of yaxis, e.g. y, y2, y3, y4, ...
     * }
     * ```
     * 
     * @param {DataFrame} dataframe 
     * @param {string[]|object[]} y names of dataframe columns for y-axes
     * @param {string} x name of dataframe column for the x-axis
     * @param {string} z name of dataframe column for the z-axis
     */
    plot(dataframe, y = [], x = null, z = null) {
        // prepare plot data
        if (this.options.plot.type == 'pie') {
            this.pie(dataframe, y, x);
        } else {
            this.scatter(dataframe, y, x);
        }

        // plot
        this._plot();
    }

    /**
     * Plots a scatter plot from the dataframe columns defined by y with optional
     * x-axis. Columns for y-axes can be defined as strings or objects. Objects
     * can be used to style the y-axes. There<fore 
     * 
     * ```
     * y: {
     *   id: '',    -- optional
     *   name: '',  -- name of column in dataframe
     *   title: '', -- optional
     *   mode: '',  -- select mode for line chart, scatter plot, area chart
     *   yaxis: ''  -- optional, define name of yaxis, e.g. y, y2, y3, y4, ...
     * }
     * ```
     * 
     * @param {DataFrame} dataframe 
     * @param {string[]|object[]} y names of dataframe columns for y-axes
     * @param {string} x name of dataframe column for the x-axis
     */
    scatter(dataframe, y = [], x = null) {
        // prepare y
        let series = this._checkSeries(dataframe, y);

        // prepare x axis
        let xSeries = null;
        if (x) {
            // column-based x-axis
            if (dataframe.columns.indexOf(x) == -1) throw new Error('Column for x-axis is not defined.');
            xSeries = dataframe.series[x];

        } else {
            // index-based x-axis for each row
            xSeries = [];
            for (let i = 0; i < dataframe.rows.length; i++) {
                xSeries.push(i);
            }
        }

        let yaxes = {};
        for (let i = 0; i < series.length; i++) {
            let column = series[i];

            // define traces
            let ySeries = dataframe.series[dataframe.columnMap[series[i].name]];
            let trace = Object.assign({}, this.options.trace, column, {
                x: xSeries,
                y: ySeries,
                dataframe: dataframe
            });

            // count y-axes
            yaxes[trace.yaxis] = null;

            this.data.push(trace);
        }

        // set x-axis domain for multiple y-axes
        this.options.layout.xaxis.domain = [0, 1 - (Object.keys(yaxes).length - 1) / 10];

        // plot
        this._plot();
    }

    /**
     * Plots a pie chart from the dataframe columns defined by y.
     * Columns for y-axes can be defined as strings or objects. Objects
     * can be used to style the y-axes. There<fore 
     * 
     * ```
     * y: {
     *   id: '',    -- optional
     *   name: '',  -- name of column in dataframe
     *   title: '', -- optional
     *   mode: '',  -- select mode for line chart, scatter plot, area chart
     *   yaxis: ''  -- optional, define name of yaxis, e.g. y, y2, y3, y4, ...
     * }
     * ```
     * 
     * @param {DataFrame} dataframe 
     * @param {string[]|object[]} y names of dataframe columns for y-axes
     */
    pie(dataframe, y = []) {
        // prepare y
        let series = this._checkSeries(dataframe, y);

        // calculate sums per column
        // TODO decide if this is feasable, count instead? prepared stats?
        let values = new Array(series.length).fill(0);
        for (let i = 0; i < dataframe.rows.length; i++) {
            let row = dataframe.rows[i];
            for (let j = 0; j < series.length; j++) {
                let column = dataframe.columnMap[series[j].name];
                let value = row[column];
                if (!isNaN(value)) values[j] += value;
            }
        }

        let labels = series.map(x => x.name);

        let trace = {
            values: values,
            labels: labels,
            columns: series,
            type: 'pie',
            dataframe: dataframe
        };

        this.data.push(trace);

        // plot
        this._plot();
    }

    /**
     * Plots a box plot from the dataframe columns defined by y group with
     * given column or function.
     * Columns for y-axes can be defined as strings or objects. Objects
     * can be used to style the y-axes. There<fore 
     * 
     * ```
     * y: {
     *   id: '',    -- optional
     *   name: '',  -- name of column in dataframe
     *   title: ''  -- optional
     * }
     * ```
     * 
     * @param {DataFrame} dataframe 
     * @param {string[]|object[]} y names of dataframe columns for y-axes
     * @param {string|function} grouping column or function
     */
    box(dataframe, y = [], grouping) {
        // prepare y
        let series = this._checkSeries(dataframe, y);

        let stats = dataframe.groups(grouping, y);

        for (let i = 0; i < series.length; i++) {
            let column = series[i];
            let keys = Object.keys(stats);
            let ySeries = [];
            let xSeries = [];
            for (let j = 0; j < keys.length; j++) {
                let key = keys[j];
                let s = stats[key][i];
                xSeries.push(key, key, key, key, key, key);
                ySeries.push(s.min, s.q25, s.median, s.median, s.q75, s.max);
            }

            let trace = Object.assign({}, this.options.trace, column, {
                //let trace = {
                x: xSeries,
                y: ySeries,
                dataframe: dataframe,
                type: 'box',
                boxmean: false
            });

            this.data.push(trace);
        }

        this.options.layout = Object.assign(this.options.layout, {
            title: '',
            xaxis: {
                zeroline: false
            },
            boxmode: 'group'
        });

        // plot
        this._plot();
    }

    _plot() {
        let that = this;

        if (this.initialized) {
            Plotly.redraw(this.getElement());

        } else {
            this.initialized = true;

            Plotly.newPlot(this.getElement(), this.data, this.options.layout, this.options.config);


            // add event listeners
            const element = this.getElement();
            element.on('plotly_legendclick', function (event) {
                let column = null;

                if (that.options.plot.type == 'pie') {
                    let columns = event.data[event.curveNumber].columns;
                    for (let i = 0; i < columns.length; i++) {
                        if (columns[i].name == event.label) {
                            column = columns[i];
                            break;
                        }
                    }

                    // invert state
                    column.visible = element.layout && element.layout.hiddenlabels && element.layout.hiddenlabels.indexOf(event.label) != -1;

                } else {
                    column = event.data[event.curveNumber];

                    // invert state
                    column.visible = column.visible !== undefined && column.visible !== true;
                }

                that.fire('legend', {
                    column: column,
                    visible: column.visible,
                    original: event
                });
            });


            // FIXME fire useful event data and scale/domain windows
            this.getElement().on('plotly_relayout', function (event) {
                that.fire('scaled', {
                    original: event
                });
            });

            // plotly_click, plotly_hover, plotly_unhover, plotly_selecting, plotly_selected, 
            // plotly_relayout, plotly_legendclick, plotly_legenddoubleclick, plotly_restyle,
            // plotly_webglcontextlost, plotly_afterplot, plotly_autosize, plotly_deselect,
            // plotly_doubleclick, plotly_redraw, and plotly_animated.
        }
    }

    /**
     * Validates and returns prepared series from ys.
     * 
     * @param {DataFrame} dataframe 
     * @param {string[]|object[]} y 
     * @returns object[] 
     */
    _checkSeries(dataframe, y = []) {
        let series = [];

        if (!y || (y.length == 0)) {
            series = dataframe.columns.slice();
        } else {
            series = y.slice();
        }

        for (let i = 0; i < series.length; i++) {
            let column = series[i];
            if (typeof column === 'string') {
                column = series[i] = {
                    id: series[i],
                    name: series[i]
                }
            }

            if (dataframe.columns.indexOf(column.name) == -1) throw new Error('Column for y ' + column.name + ' is not defined.');
        }

        return series;
    }

    /**
     * Sets the visibility of the given columns names (y). By default given
     * columns will be shown. Set optional visible to false to drop the column
     * from the plot completely or to "legendonly" to leave a toggle in the legend.
     * 
     * @param {string[]} y 
     * @param {boolean|string} visible 
     */
    show(y = [], visible = true) {
        if (!this.initialized) return;
        if (!Array.isArray(y)) y = [y];

        const element = this.getElement();
        if (this.options.plot.type == 'pie') {
            if (!element.layout) element.layout = {};
            if (!element.layout.hiddenlabels) element.layout.hiddenlabels = [];
            let hidden = element.layout.hiddenlabels;
            if (visible == true) {
                for (let i = 0; i < y.length; i++) {
                    let pos = hidden.indexOf(y[i]);
                    if (pos > -1) hidden.splice(pos, 1);
                }
            } else {
                for (let i = 0; i < y.length; i++) {
                    hidden.push(y[i]);
                }
            }

        } else {
            let traces = element.data;
            for (let i = 0; i < traces.length; i++) {
                if (y.indexOf(traces[i].id) != -1) {
                    traces[i].visible = visible;
                }
            }
        }

        Plotly.redraw(this.getElement());
    }

    /**
     * Hides the given columns in the plot but leaves an entry in the legend.
     * 
     * @param {string[]} y 
     */
    hide(y = []) {
        this.show(y, 'legendonly');
    }

    clear() {

    }
}